import { useEffect, useState } from "react";
import { Star, MoreVertical, Pencil, Trash2, BookmarkPlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { presetRange } from "@/lib/auditDates";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const DEFAULTS = { dfield: "received", dir: "desc" };

// Resuelve un favorito a params de URL; los presets relativos se recalculan al día de hoy.
export function resolveFavorite(filters) {
  const out = { ...filters };
  delete out.view;
  if (out.preset && out.preset !== "custom") {
    const r = presetRange(out.preset);
    if (r) [out.from, out.to] = r;
  }
  return out;
}

const norm = (o) => {
  const x = { ...DEFAULTS, ...o };
  return JSON.stringify(Object.keys(x).sort().filter((k) => x[k]).map((k) => [k, x[k]]));
};

const NameDialog = ({ open, title, initial, onClose, onSubmit }) => {
  const [name, setName] = useState(initial);
  useEffect(() => { if (open) setName(initial); }, [open, initial]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="favorite-name-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) onSubmit(name.trim()); }} className="space-y-4">
          <input autoFocus value={name} maxLength={60} onChange={(e) => setName(e.target.value)}
            placeholder="Ej.: Fernando · semana pasada" className="input-base w-full" data-testid="favorite-name-input" />
          <DialogFooter className="gap-2">
            <button type="button" onClick={onClose} className="btn-secondary" data-testid="favorite-name-cancel">Cancelar</button>
            <button type="submit" disabled={!name.trim()} className="btn-primary" data-testid="favorite-name-save">Guardar</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const FavoriteChip = ({ fav, active, onApply, onRename, onDelete }) => (
  <div className={`shrink-0 inline-flex items-center rounded-full border transition-colors ${active ? "bg-amber-50 border-amber-400" : "bg-white border-slate-300 hover:border-slate-500"}`}
    data-testid={`favorite-chip-${fav.id}`}>
    <button type="button" onClick={() => onApply(fav)} className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1.5 text-xs font-semibold text-slate-800 whitespace-nowrap" data-testid={`favorite-apply-${fav.id}`}>
      <Star size={13} className={active ? "fill-amber-400 text-amber-500" : "text-amber-500"} /> {fav.name}
    </button>
    <DropdownMenu>
      <DropdownMenuTrigger className="px-1.5 py-1.5 text-slate-400 hover:text-slate-800" data-testid={`favorite-menu-${fav.id}`} aria-label="Opciones">
        <MoreVertical size={14} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onRename(fav)} data-testid={`favorite-rename-${fav.id}`}><Pencil size={14} className="mr-2" /> Renombrar</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onDelete(fav)} className="text-red-600" data-testid={`favorite-delete-${fav.id}`}><Trash2 size={14} className="mr-2" /> Eliminar</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
);

export const FavoriteFilters = ({ current, view, suggestedName, onApply }) => {
  const [favs, setFavs] = useState([]);
  const [dialog, setDialog] = useState(null);

  useEffect(() => { api.get("/orders/filter-favorites").then((r) => setFavs(r.data)).catch(() => {}); }, []);

  async function save(name) {
    try {
      if (dialog.fav) {
        const r = await api.patch(`/orders/filter-favorites/${dialog.fav.id}`, { name });
        setFavs((l) => l.map((x) => (x.id === r.data.id ? r.data : x)));
        toast.success("Favorito renombrado");
      } else {
        const r = await api.post("/orders/filter-favorites", { name, filters: { ...current, view } });
        setFavs((l) => [...l, r.data]);
        toast.success("Filtro guardado en favoritos");
      }
      setDialog(null);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function remove(fav) {
    if (!window.confirm(`¿Eliminar el favorito "${fav.name}"?`)) return;
    try {
      await api.delete(`/orders/filter-favorites/${fav.id}`);
      setFavs((l) => l.filter((x) => x.id !== fav.id));
      toast.success("Favorito eliminado");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const currentKey = norm(current);
  return (
    <div className="flex items-center gap-2 min-w-0" data-testid="favorite-filters">
      <div className="flex flex-nowrap gap-1.5 overflow-x-auto scroll-smooth pb-1 -mx-1 px-1 flex-1 min-w-0 [scrollbar-width:thin]">
        {favs.length === 0 && <span className="text-xs text-slate-400 py-1.5 whitespace-nowrap" data-testid="favorite-empty">Sin favoritos guardados</span>}
        {favs.map((fav) => (
          <FavoriteChip key={fav.id} fav={fav} active={norm(resolveFavorite(fav.filters)) === currentKey}
            onApply={onApply} onRename={(x) => setDialog({ title: "Renombrar favorito", initial: x.name, fav: x })} onDelete={remove} />
        ))}
      </div>
      <button type="button" onClick={() => setDialog({ title: "Guardar filtro favorito", initial: suggestedName })}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-dashed border-slate-400 text-slate-700 hover:border-slate-800 hover:text-slate-900 transition-colors whitespace-nowrap"
        data-testid="favorite-save-btn">
        <BookmarkPlus size={14} /> Guardar filtro
      </button>
      <NameDialog open={!!dialog} title={dialog?.title} initial={dialog?.initial || ""} onClose={() => setDialog(null)} onSubmit={save} />
    </div>
  );
};
